import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AuthData } from '../../../../context/auth';
import {
	EndorsementData,
	getDisplayedExposures,
	withEndorsementContext,
} from '../../../../context/EndorsementProvider';
import { Exposure } from '../../../../types/policy';
import { tableStyles } from '../../../../util/styles';
import {
	getComparator,
	HeadCell,
	Order,
	stableSort,
} from '../../../../util/tableUtil';
import { StyledEmptyMessage, StyledTableActions } from '../../../common';
import SortableTable from '../../../SortableTable';
import { getExposureDisplayNameAndUrl } from '../util';

interface Props {
	exposures: Exposure[];
	authData: AuthData;
	endorsementData: EndorsementData;
	openConfirmationModal: (exposure: Exposure) => void;
}

interface Data {
	exposureId: string;
	exposureName: string;
	dateCreated: string;
	actions: any;
	link: string;
}

function createData(
	exposureId: string,
	exposureName: string,
	dateCreated: string,
	actions: any,
	link: string,
): Data {
	return { exposureId, exposureName, dateCreated, actions, link };
}

/**
 * The component to render a table  of exposures with the given the exposure list.
 */
const ExposuresList = (props: Props) => {
	const { t } = useTranslation();
	const { exposures, endorsementData, authData, openConfirmationModal } = props;

	if (!exposures) {
		return null;
	}

	const { policy, endorsementUpdateObj, productConfig } = endorsementData;
	const currentExposures = exposures;
	const displayedExposures = getDisplayedExposures(
		currentExposures,
		endorsementUpdateObj,
	);
	const isEditable =
		authData.hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';

	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('dateCreated');

	const emptyTable = !displayedExposures.length ? (
		<StyledEmptyMessage>No {t('exposure.plural.upper')}</StyledEmptyMessage>
	) : null;
	const classes = tableStyles();

	const headCells: HeadCell[] = [
		{
			id: 'exposureName',
			rightAlign: false,
			label: `${t('exposure.singular.upper')} Name`,
		},
		{ id: 'dateCreated', rightAlign: false, label: 'Date Created' },
	];

	if (isEditable) {
		headCells.push({ id: 'actions', rightAlign: false, label: 'Actions' });
	}

	const rows: any = displayedExposures.map((exposure) => {
		const { urlId, exposureDisplayName } = getExposureDisplayNameAndUrl(
			exposure,
			endorsementUpdateObj,
			productConfig,
		);

		const createdTimestamp = (exposure as Exposure).createdTimestamp
			? (exposure as Exposure).createdTimestamp
			: Date.now();
		const removeOnClick = (e: any) => {
			e.preventDefault();
			e.stopPropagation();
			openConfirmationModal(exposure as Exposure as Exposure);
		};

		const actions = (
			<StyledTableActions>
				{isEditable && <a onClick={removeOnClick}>Remove</a>}
			</StyledTableActions>
		);
		return createData(
			urlId,
			exposureDisplayName,
			`${createdTimestamp}`,
			actions,
			`/policy/${policy.locator}/endorsements/${endorsementUpdateObj.locator}/exposures/${urlId}`,
		);
	});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			return (
				<TableRow
					hover
					tabIndex={-1}
					key={row.exposureId}
					component={Link}
					to={row.link}
					className={classes.tableRow}
				>
					<TableCell>{row.exposureName}</TableCell>
					<TableCell>{authData.getFormattedDate(row.dateCreated)}</TableCell>
					{isEditable && <TableCell>{row.actions}</TableCell>}
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withEndorsementContext<Props>(ExposuresList);
